import { getStyledWrapper } from "@insite/client-framework/Common/StyledWrapper";
import Button from "@insite/mobius/Button";
import Modal from "@insite/mobius/Modal";
import Typography from "@insite/mobius/Typography";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import { downloadExcel } from "@MasterBluePrint/Common/Utilities/DownloadExcel";
import { Cart } from "@MasterBluePrint/Overrides/Services/CartService";
import React, { FC, useState } from "react";
import { css } from "styled-components";

export interface DownloadCartLineModalStyles {
    actionsWrapper?: InjectableCss;
    fileFormatWrapper?: InjectableCss;
    fileFormatLabel?: InjectableCss;
}

export const downloadCartLineModalStyles: DownloadCartLineModalStyles = {
    fileFormatLabel: {
        css: css`
            font-weight: 600;
            margin-top: 12px;
        `,
    },
    fileFormatWrapper: {
        css: css`
            margin-top: 16px;
        `,
    },
    actionsWrapper: {
        css: css`
            display: flex;
            justify-content: flex-end;
            margin-top: 20px;
            gap: 10px;
        `,
    },
};

const StyledSection = getStyledWrapper("section");
const styles = downloadCartLineModalStyles;

interface CartDownloadRow {
    Line: number | null;
    "Part Number": string;
    "Manufacturer Part Number": string;
    Description: string;
    Quantity: number;
    "Unit Price": number;
    "Extended Price USD": number;
}

interface DownloadCartLineModalProps {
    isOpen: boolean;
    onClose: () => void;
    cart: Readonly<Cart> | undefined;
}

const DownloadCartLineModal: FC<DownloadCartLineModalProps> = ({ isOpen, onClose, cart }) => {
    const [downloadFormat, setDownloadFormat] = useState<"xlsx" | "csv">("xlsx");

    const handleConfirmDownload = () => {
        if (!cart?.cartLines?.length) {
            return;
        }

        const rows: CartDownloadRow[] = cart.cartLines.map((line, index) => ({
            Line: line.line,
            "Part Number": line.erpNumber ?? "",
            "Manufacturer Part Number": line.manufacturerItem ?? "",
            Description: line.shortDescription ?? "",
            Quantity: line.qtyOrdered ?? 0,
            "Unit Price": line.pricing?.unitNetPrice ?? 0,
            "Extended Price USD": line.pricing?.extendedUnitNetPrice ?? 0,
        }));

        if (downloadFormat === "xlsx") {
            downloadExcel(rows, "cart.xlsx", "Cart");
        } else if (downloadFormat === "csv") {
            downloadExcel(rows, "cart.csv", "Cart");
        }
        onClose();
    };

    return (
        <Modal isOpen={isOpen} handleClose={onClose} headline="Download Cart" size={500}>
            <Typography as="p">Select the file format you would like to download.</Typography>
            <Typography as="p" style={{ fontWeight: 600, marginTop: 12 }}>
                File Format
            </Typography>
            <div style={{ marginTop: 16 }}>
                <label>
                    <input
                        type="radio"
                        checked={downloadFormat === "xlsx"}
                        onChange={() => setDownloadFormat("xlsx")}
                    />
                    &nbsp;.xlsx
                </label>
                <br />
                <label>
                    <input type="radio" checked={downloadFormat === "csv"} onChange={() => setDownloadFormat("csv")} />
                    &nbsp;.csv
                </label>
            </div>

            <StyledSection {...styles.actionsWrapper}>
                <Button buttonType="outline" onClick={onClose}>
                    Cancel
                </Button>
                <Button variant="primary" onClick={handleConfirmDownload}>
                    Download
                </Button>
            </StyledSection>
        </Modal>
    );
};

export default DownloadCartLineModal;
